import { memo, useCallback, useContext, useEffect, useRef, useState } from "react"
import { AppContext } from "../../../App"
import { ChartEvents } from "../chart.events"
import { useDispatch } from "react-redux"
import { openConnectAccountDialog, updateOrderHistory } from "../../../reducer/orders.reducer"
import { Broker_OrderType, Broker_Variety, Order, OrderStatus, OrderType, Product, TransactionType, Validity } from "../../../proto/generated/order"
import { PositionComponentTypes } from "../drawingTools/ordersOnChartTool/OrdersOnChartTool"
import { Position } from "../../../proto/generated/position"
import { Security } from "../../../proto/generated/candle-data-depot-common"
import { selectPrimaryTradingAccount, selectTradingAccounts } from "../../../selectors/user"
import { cancelOrder, placeOrModifyOrder } from "../../../services/apis/orders"
import commonActions from "../../../actions/common.actions"
import ReusableDialog from "../../common/reusableDialog"
import { getSecurityAsync } from "../../../util/security"
import { ExtraWideTooltip } from "../../custom"
import { Avatar, Box, Card, CardContent, CardHeader, Typography } from "@mui/material"
import { getDisplayDateTime } from "../../../util/dateTime"
import { PlaceOrModifyOrdersRequest, TradingAccountStatus } from "../../../proto/generated/trading-bridge"
import { addOrderAlertMessage } from "../../../util/orders"
import { v4 as uuid } from "uuid"
import { selectIsEmbedded } from "../../../selectors/device"
import { callNative } from "../../../util/nativeBridge"
import { selectPositions } from "../../../selectors/positions"
import { selectPrimarySecurity } from "../../../selectors/home"
import store from "../../../store"
import { selectChartTradingSettings } from "../../../selectors/chart"

type Props = {
    chartId: string
}

type EditOrderData = {
    orders: Order[]
    type: 'reverse' | 'cancel' | 'close' | 'place'
}

const OrderActionsOnChart = ({ chartId }: Props) => {
    const dispatch = useDispatch()
    const appContextRef = useRef(useContext(AppContext))
    const [editOrderData, setEditOrderData] = useState<EditOrderData | undefined>()
    const editOrderDataRef = useRef<EditOrderData | undefined>()
    editOrderDataRef.current = editOrderData
    const [security, setSecurity] = useState<Awaited<ReturnType<typeof getSecurityAsync>>>(undefined)
    const [ordersMarkerData, setOrdersMarkerData] = useState<{
        x: number
        y: number
        totalQuantity: number
        averagePrice: number
        type: 'buy' | 'sell'
        time: number
        orders: Order[]
    } | null>(null)

    useEffect(() => {
        let prevKey: string | undefined
        const syncSecurity = () => {
            const key = selectPrimarySecurity(store.getState()).key
            if (!key || key === prevKey) return
            prevKey = key
            getSecurityAsync(key).then(setSecurity)
        }
        syncSecurity()
        return store.subscribe(syncSecurity)
    }, [])

    const handleEditOrder = useCallback(async (data?: EditOrderData) => {
        if (!data?.orders?.length) return

        const primaryTradingAccount = selectPrimaryTradingAccount(store.getState())
        const tradingAccounts = selectTradingAccounts(store.getState())

        if (!primaryTradingAccount || !tradingAccounts || tradingAccounts.length === 0) {
            dispatch(openConnectAccountDialog({
                type: 'connect',
                tradingAccountId: ''
            }))
        } else if (primaryTradingAccount.status !== TradingAccountStatus.ACTIVE) {
            dispatch(openConnectAccountDialog({
                type: 'reconnect',
                tradingAccountId: primaryTradingAccount.accountId
            }))
        }

        if (data.type === 'cancel') {
            const res = await cancelOrder({
                dispatch,
                body: {
                    tradingAccountId: primaryTradingAccount?.accountId ?? "",
                    orderId: data.orders.map(d => d.orderId)
                }
            })
            const responseOrder = res.orderResponse[0]
            if (!responseOrder) return
            const { successful, commitOrder, errorMessage } = responseOrder

            if (!successful) {
                commonActions.setApiError(dispatch, {
                    error: true,
                    message: errorMessage || 'Unable to cancel order',
                    errorType: 'error'
                })
                return
            } else if (commitOrder) {
                dispatch(updateOrderHistory({ updates: data.orders.map(order => ({ ...order, status: OrderStatus.CANCELLED })) }))
            }
        } else {
            const requestOrders: Order[] = data.orders
            const request: PlaceOrModifyOrdersRequest = {
                tradingAccountId: primaryTradingAccount?.accountId ?? '',
                order: requestOrders,
                buyBeforeSell: false
            }

            try {
                const response = await placeOrModifyOrder(request)
                const updates: Order[] = []
                ;(response.orderResponse || []).forEach((orderResponse, i) => {
                    const { successful, commitOrder, errorMessage, order } = orderResponse
                    if (!successful) {
                        addOrderAlertMessage({
                            ...request.order[i],
                            status: OrderStatus.FAILED,
                            message: errorMessage || 'Unable to place order'
                        })
                    } else if (order) {
                        updates.push(order)
                        addOrderAlertMessage({
                            ...order,
                            message: 'Order placed successfully'
                        })
                    }
                })
                updates.length && dispatch(updateOrderHistory({ updates }))
            } catch (error: any) {
                addOrderAlertMessage({
                    ...request.order[0],
                    status: OrderStatus.FAILED,
                    message: error.message || 'Unable to place order'
                })
            }
        }
    }, [dispatch])

    const closeOrReversePosition = useCallback((positionSecurity: Security, transactionType: TransactionType, quantity: number, reverse = false, product: Product) => {
        const batchId = uuid()
        const clientOrderId = uuid()
        const data: EditOrderData = {
            orders: [{
                security: positionSecurity,
                transactionType,
                orderType: OrderType.NORMAL,
                brokerOrderType: Broker_OrderType.MARKET_ORDER,
                quantity: quantity * (reverse ? 2 : 1),
                isBasket: false,
                batchId,
                orderDate: Date.now(),
                product: product ?? Product.CNC,
                price: 0,
                triggerPrice: 0,
                brokerVariety: Broker_Variety.REGULAR,
                validity: Validity.DAY,
                clientOrderId,
                immediateExecutePaperOrder: false
            }],
            type: reverse ? 'reverse' : 'close'
        }
        const oneClickTrading = selectChartTradingSettings(chartId)(store.getState())?.oneClickTrading
        if (oneClickTrading) {
            handleEditOrder(data)
        } else {
            setEditOrderData(data)
        }
    }, [chartId, handleEditOrder])

    const onBasketPositionClose = useCallback((position: Position) => {
        if (!position) return
        const positions = selectPositions(store.getState())
        const basketPositions = positions.filter(p => p.basketId === position.basketId && p.closePosition !== 0)

        const orders: Order[] = []
        if (basketPositions.length > 0) {
            const batchId = uuid()
            const basketId = basketPositions[0].basketId
            basketPositions.forEach((pos) => {
                const clientOrderId = uuid()
                const quantity = Math.abs(pos.closePosition)
                const transactionType: TransactionType = pos.closePosition > 0 ? TransactionType.SELL : TransactionType.BUY
                orders.push({
                    security: pos.security,
                    transactionType,
                    orderType: OrderType.NORMAL,
                    brokerOrderType: Broker_OrderType.MARKET_ORDER,
                    quantity,
                    isBasket: true,
                    batchId,
                    orderDate: Date.now(),
                    product: pos.product ?? Product.CNC,
                    price: 0,
                    triggerPrice: 0,
                    brokerVariety: Broker_Variety.REGULAR,
                    validity: Validity.DAY,
                    clientOrderId,
                    immediateExecutePaperOrder: false,
                    basketId,
                    strategyGroup: pos.strategyGroup
                })
            })
        }
        const data: EditOrderData = { orders, type: 'close' }
        const oneClickTrading = selectChartTradingSettings(chartId)(store.getState())?.oneClickTrading
        if (oneClickTrading) {
            handleEditOrder(data)
        } else {
            setEditOrderData(data)
        }
    }, [chartId, handleEditOrder])

    const onChartOrderActionClicked = useCallback((event: CustomEvent) => {
        if (event.type !== ChartEvents.CHART_ORDER_ACTION_CLICKED) return

        const isEmbedded = selectIsEmbedded(store.getState())
        const primaryTradingAccount = selectPrimaryTradingAccount(store.getState())
        const accountId = primaryTradingAccount?.accountId ?? ''

        if (event.detail.actionType === PositionComponentTypes.CONFIRM_BTN) {
            const orders: Order[] = []
            if (event.detail?.slOrder) orders.push(event.detail?.slOrder)
            if (event.detail?.tOrder) orders.push(event.detail?.tOrder)
            if (event.detail?.order) orders.push(event.detail?.order)
            if (orders.length) {
                handleEditOrder({ orders, type: 'place' })
            }
        } else if (event.detail.actionType === PositionComponentTypes.QUANTITY_BTN && event.detail.key.includes('open-order')) {
            console.log(event.detail, 'Add Qunatity Change popup')
        } else if (event.detail.actionType === PositionComponentTypes.CANCEL_BTN || event.detail.actionType === PositionComponentTypes.REVERSE_BTN) {
            const position = event.detail.position as Position
            const isBasket = event.detail.isBasket
            if (position) {
                const transactionType: TransactionType = position.closePosition < 0 ? TransactionType.SELL : position.closePosition > 0 ? TransactionType.BUY : undefined
                if (isEmbedded) {
                    if (event.detail.actionType === PositionComponentTypes.REVERSE_BTN) {
                        callNative('onReversePositionDialogClicked', JSON.stringify({
                            tradingAccountId: accountId,
                            order: [{
                                security: position.security,
                                transactionType,
                                orderType: OrderType.NORMAL,
                                brokerOrderType: Broker_OrderType.MARKET_ORDER,
                                quantity: Math.abs(position.closePosition) * 2,
                                isBasket: false,
                                batchId: uuid(),
                                orderDate: Date.now(),
                                product: position.product ?? Product.CNC,
                                price: 0,
                                triggerPrice: 0,
                                brokerVariety: Broker_Variety.REGULAR,
                                validity: Validity.DAY,
                                clientOrderId: uuid()
                            }]
                        }))
                    } else if (event.detail.actionType === PositionComponentTypes.CANCEL_BTN) {
                        callNative('onClosePositionDialogClicked', JSON.stringify({
                            tradingAccountId: accountId,
                            order: [{
                                security: position.security,
                                transactionType,
                                orderType: OrderType.NORMAL,
                                brokerOrderType: Broker_OrderType.MARKET_ORDER,
                                quantity: Math.abs(position.closePosition),
                                isBasket: false,
                                batchId: uuid(),
                                orderDate: Date.now(),
                                product: position.product ?? Product.CNC,
                                price: 0,
                                triggerPrice: 0,
                                brokerVariety: Broker_Variety.REGULAR,
                                validity: Validity.DAY,
                                clientOrderId: uuid()
                            }]
                        }))
                    }
                } else if (isBasket) {
                    onBasketPositionClose(position)
                } else {
                    closeOrReversePosition(
                        position.security,
                        transactionType === TransactionType.BUY ? TransactionType.SELL : TransactionType.BUY,
                        Math.abs(position.closePosition),
                        event.detail.actionType === PositionComponentTypes.REVERSE_BTN,
                        position.product
                    )
                }
            } else {
                const order = event.detail.order as Order
                if (isEmbedded && order?.orderId) {
                    callNative('onCancelOrdersDialogClicked', JSON.stringify({
                        tradingAccountId: accountId,
                        orderId: [order?.orderId ?? '']
                    }))
                } else if (!isEmbedded) {
                    const data: EditOrderData = { orders: [order], type: 'cancel' }
                    const oneClickTrading = selectChartTradingSettings(chartId)(store.getState())?.oneClickTrading
                    if (oneClickTrading) {
                        handleEditOrder(data)
                    } else {
                        setEditOrderData(data)
                    }
                }
            }
        }
    }, [chartId, closeOrReversePosition, handleEditOrder, onBasketPositionClose])

    const onChartOrderMarkerClicked = useCallback((event: CustomEvent) => {
        const isEmbedded = selectIsEmbedded(store.getState())
        if (isEmbedded) {
            callNative('onChartOrderMarkerClicked', JSON.stringify({
                totalQuantity: event.detail.totalQuantity,
                averagePrice: event.detail.averagePrice,
                type: event.detail.type,
                orders: event.detail.orders.map((order: Order) => ({
                    quantity: order.quantity,
                    price: order.averageTradedPrice,
                    time: order.exchangeTimestamp,
                }))
            }))
        } else {
            setOrdersMarkerData(event.detail)
        }
    }, [])

    useEffect(() => {
        const chart = appContextRef.current.getChart(chartId)
        if (!chart) return

        chart.subscribeEvent(ChartEvents.CHART_ORDER_ACTION_CLICKED, onChartOrderActionClicked)
        chart.subscribeEvent(ChartEvents.CHART_ORDER_MARKER_CLICKED, onChartOrderMarkerClicked)

        return () => {
            chart.unsubscribeEvent(ChartEvents.CHART_ORDER_ACTION_CLICKED, onChartOrderActionClicked)
            chart.unsubscribeEvent(ChartEvents.CHART_ORDER_MARKER_CLICKED, onChartOrderMarkerClicked)
        }
    }, [chartId, onChartOrderActionClicked, onChartOrderMarkerClicked])

    return (
        <div style={{ position: 'absolute', top: 0, left: 0 }}>
            <ReusableDialog
                title={
                    editOrderData?.type === 'cancel' ?
                        "Cancel Order" :
                        editOrderData?.type === 'close' ?
                            "Close Position" :
                            editOrderData?.type === 'reverse' ?
                                "Reverse Position" : ""
                }
                open={Boolean(editOrderData)}
                onClose={() => setEditOrderData(undefined)}
                cancelText="No"
                submitText={'Yes'}
                onSubmit={() => handleEditOrder(editOrderDataRef.current)}
                content={
                    editOrderData?.type === 'cancel' ?
                        "Are you sure you want to cancel the order?" :
                        editOrderData?.type === 'close' ?
                            "Are you sure you want to close position?" :
                            editOrderData?.type === 'reverse' ?
                                "Are you sure you want to reverse position?" : ""
                }
            />
            {
                ordersMarkerData ? (
                    <ExtraWideTooltip open={true} placement="top-start" title={(
                        <Card className="font-feature-num" sx={{ borderLeft: theme => `4px solid ${theme.palette?.[ordersMarkerData.type]?.main}` }}>
                            <CardHeader
                                sx={{
                                    '& .MuiCardHeader-avatar': {
                                        mr: '10px'
                                    }
                                }}
                                avatar={
                                    <Avatar aria-label="contract-switch" sx={{
                                        backgroundColor: theme => theme.palette?.[ordersMarkerData.type]?.main,
                                        width: 24,
                                        height: 24,
                                    }}>
                                        <Typography variant="h6" fontSize={'14px'} sx={{ color: 'white' }}>{ordersMarkerData.totalQuantity}</Typography>
                                    </Avatar>
                                }
                                title={<Typography variant="h6" fontSize={'18px'}>{ordersMarkerData.type === 'buy' ? 'Buy' : 'Sell'}</Typography>} />
                            <CardContent sx={{ minWidth: 350, display: 'flex', flexDirection: 'column', gap: '4px', pt: 0 }}>
                                <Box sx={{ display: 'flex', width: '100%', pb: ordersMarkerData.orders.length > 1 ? '10px' : 0 }}>
                                    <Typography color={'textSecondary'} fontSize={'14px'}>{ordersMarkerData.totalQuantity} @ {(ordersMarkerData.averagePrice / (security?.priceMultiplier ?? 100)).toFixed(2)} avg price</Typography>
                                </Box>
                                {
                                    ordersMarkerData.orders.length > 1 ? (
                                        <>
                                            <Box sx={{ display: 'flex', width: '100%' }}>
                                                <Typography color={'textSecondary'} fontSize={'14px'} fontWeight={'bold'}>TRADES</Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', width: '100%', flexDirection: 'column' }}>
                                                {
                                                    ordersMarkerData.orders.map((order) => (
                                                        <Typography key={order.orderId ?? `${order.exchangeTimestamp}-${order.quantity}`} color={'textSecondary'} fontSize={'14px'}>{`${order.quantity} @ ${(order.averageTradedPrice / (security?.priceMultiplier ?? 100))?.toFixed(2)} ${getDisplayDateTime(order.exchangeTimestamp)}`}</Typography>
                                                    ))
                                                }
                                            </Box>
                                        </>
                                    ) : null
                                }
                            </CardContent>
                        </Card>
                    )} sx={{ margin: '0px !important', padding: '0px !important' }}>
                        <Box sx={{ position: 'absolute', left: ordersMarkerData?.x, top: ordersMarkerData?.y }} />
                    </ExtraWideTooltip>
                ) : null
            }
        </div>
    )
}

export default memo(OrderActionsOnChart)